import { CanvasNode, HistoryItem, LightHistoryItem, MODEL_PRICING, NodeType, RecentFileMetadata } from '../types';
import { historyItemFields } from '../utils/historyItems';

export type AnalyticsPreset = '1year' | '1month' | '1week' | 'today' | 'yesterday' | 'custom';

export interface DateRange {
  start: number;
  end: number;
}

export interface AnalyticsSummary {
  totalNodes: number;
  nodeTypeCounts: Record<NodeType, number>;
  modelsUsed: Record<string, number>;
  estimatedCost: number;
  totalSessions: number;
  topWorkspaces: { name: string; lastSaved: number; nodeCount?: number }[];
}

export interface AnalyticsInputs {
  nodes: CanvasNode[];
  history: (HistoryItem | LightHistoryItem)[];
  timeframe: DateRange;
  rateAssumptions: Record<string, number>;
  recentFiles: RecentFileMetadata[];
}

const PRESET_OFFSETS: Record<AnalyticsPreset, number | null> = {
  '1year': 365 * 24 * 60 * 60 * 1000,
  '1month': 30 * 24 * 60 * 60 * 1000,
  '1week': 7 * 24 * 60 * 60 * 1000,
  today: 0,
  yesterday: 24 * 60 * 60 * 1000,
  custom: null,
};

export const getPresetRange = (preset: AnalyticsPreset, custom?: DateRange): DateRange => {
  const now = Date.now();
  if (preset === 'custom' && custom && custom.start && custom.end) {
    return {
      start: Math.min(custom.start, custom.end),
      end: Math.max(custom.start, custom.end),
    };
  }

  const offset = PRESET_OFFSETS[preset];
  if (offset === null) {
    return { start: now - 7 * 24 * 60 * 60 * 1000, end: now };
  }

  if (preset === 'today') {
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    return { start: startOfDay.getTime(), end: now };
  }

  if (preset === 'yesterday') {
    const startOfDay = new Date(now - 24 * 60 * 60 * 1000);
    startOfDay.setHours(0, 0, 0, 0);
    return { start: startOfDay.getTime(), end: startOfDay.getTime() + 24 * 60 * 60 * 1000 - 1 };
  }

  return { start: now - offset, end: now };
};

const extractModels = (item: HistoryItem | LightHistoryItem): string[] => {
  const models = new Set<string>();
  const data = 'data' in item ? item.data : (item as HistoryItem).data;

  const candidate = (value?: string) => {
    if (value) models.add(value);
  };

  if (data) {
    candidate(data.writeData?.modelId);
    candidate(data.videoConfig?.model);
    candidate(data.imageGenConfig?.model);
    candidate(data.scriptConfig?.model);
    candidate(data.storyboardConfig?.model);
    candidate(data.entityData?.model);
    candidate(data.prompt);
  }
  if ('prompt' in item && item.prompt) {
    models.add(item.prompt);
  }

  return Array.from(models).filter(Boolean);
};

const deriveCostFromModels = (models: string[], assumptions: Record<string, number>) => {
  return models.reduce((sum, model) => {
    const rate = assumptions[model] ?? MODEL_PRICING[model] ?? 0;
    return sum + rate;
  }, 0);
};

export const computeAnalyticsSummary = ({ nodes, history, timeframe, rateAssumptions, recentFiles }: AnalyticsInputs): AnalyticsSummary => {
  const { start, end } = timeframe;
  const filtered = history.filter((item) => {
    const timestamp = item.timestamp;
    return timestamp >= start && timestamp <= end;
  });

  const totalNodes = filtered.length;
  const nodeTypeCounts: Record<NodeType, number> = Object.values(NodeType).reduce((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {} as Record<NodeType, number>);

  filtered.forEach((item) => {
    if (nodeTypeCounts[item.type] !== undefined) {
      nodeTypeCounts[item.type] += 1;
    }
  });

  if (filtered.length === 0 && nodes.length > 0) {
    nodes.forEach((node) => {
      if (nodeTypeCounts[node.type] !== undefined) {
        nodeTypeCounts[node.type] += 1;
      }
    });
  }

  const modelsUsed: Record<string, number> = {};
  let estimatedCost = 0;
  const sessionBuckets = new Set<string>();

  filtered.forEach((item) => {
    const models = extractModels(item);
    models.forEach((model) => {
      modelsUsed[model] = (modelsUsed[model] || 0) + 1;
    });

    const cost = historyItemFields(item).cost;
    estimatedCost += cost || 0;
    if (!cost && models.length > 0) {
      estimatedCost += deriveCostFromModels(models, rateAssumptions);
    }

    const ts = new Date(item.timestamp).toDateString();
    sessionBuckets.add(ts);
  });

  const sortedWorkspaces = [...recentFiles]
    .sort((a, b) => (b.lastSaved || 0) - (a.lastSaved || 0))
    .slice(0, 3)
    .map((file) => ({ name: file.name, lastSaved: file.lastSaved, nodeCount: file.nodeCount }));

  return {
    totalNodes,
    nodeTypeCounts,
    modelsUsed,
    estimatedCost,
    totalSessions: sessionBuckets.size,
    topWorkspaces: sortedWorkspaces,
  };
};